import { useCallback, useEffect, useState } from 'react';
import { projectionsApi, type ProjectionWindow } from '@/api/projections';
import { readWindow, useAppToday } from '@/shared/servedWindow';
import { horizonWindow, type HorizonMonths } from './horizon';
import {
  viewFromAccount,
  viewFromCombined,
  viewFromPlan,
  viewFromEnvelope,
  type DrillDown,
  type ProjectionScope,
  type ProjectionView,
} from './types';

export type ProjectionStatus = 'loading' | 'ready' | 'error';

export interface UseProjectionResult {
  status: ProjectionStatus;
  /** What the last failed fetch threw (read by the panel's `ErrorState`); `undefined` otherwise. */
  error: unknown;
  full: ProjectionView | null;
  table: ProjectionView | null;
  /**
   * The table's drill-down was served from a later day than it asked (TMMAPI-0188 U3: a past `from`
   * is read as today): the list says "From today" rather than implying the whole month.
   */
  fromToday: boolean;
  reload: () => Promise<void>;
}

async function fetchView(
  scope: ProjectionScope,
  window: ProjectionWindow,
): Promise<ProjectionView> {
  switch (scope.kind) {
    case 'account':
      return viewFromAccount(await projectionsApi.account(scope.id, window));
    case 'envelope':
      return viewFromEnvelope(await projectionsApi.envelope(scope.id, window));
    case 'combined':
      return viewFromCombined(await projectionsApi.combined(window, scope.accountIds));
    case 'virtualAccount':
      return viewFromPlan(
        await projectionsApi.virtualAccountPlan(scope.virtualAccountId, window, scope.envelopeId),
      );
  }
}

function toScope(
  kind: ProjectionScope['kind'],
  id: number,
  virtualAccountId: number,
  envelopeId: number | undefined,
  accountIds: string,
): ProjectionScope {
  switch (kind) {
    case 'account':
      return { kind: 'account', id };
    case 'envelope':
      return { kind: 'envelope', id };
    case 'combined':
      return {
        kind: 'combined',
        ...(accountIds.length > 0 ? { accountIds: accountIds.split(',').map(Number) } : {}),
      };
    case 'virtualAccount':
      return {
        kind: 'virtualAccount',
        virtualAccountId,
        ...(envelopeId !== undefined ? { envelopeId } : {}),
      };
  }
}

/**
 * One hook owns the horizon window, the fetch per scope, the drill-down re-fetch (same endpoint,
 * narrower `from`/`to`) and `reload()`. The graph always reads `full`; the register/table read
 * `table` (the drill window when set). A drill-down does not refetch the full window.
 */
export function useProjection(
  scope: ProjectionScope,
  horizonMonths: HorizonMonths,
  drill: DrillDown | null,
): UseProjectionResult {
  const kind = scope.kind;
  const id = scope.kind === 'account' || scope.kind === 'envelope' ? scope.id : 0;
  const virtualAccountId = scope.kind === 'virtualAccount' ? scope.virtualAccountId : 0;
  const envelopeId = scope.kind === 'virtualAccount' ? scope.envelopeId : undefined;
  const accountIds = scope.kind === 'combined' ? (scope.accountIds ?? []).join(',') : '';
  const [epoch, setEpoch] = useState(0);
  const today = useAppToday();
  const identityKey = `${kind}:${id}:${virtualAccountId}:${envelopeId ?? ''}:${accountIds}:${horizonMonths}`;
  const drillFrom = drill?.from;
  const drillTo = drill?.to;
  const drillKey =
    drillFrom !== undefined && drillTo !== undefined ? `${drillFrom}|${drillTo}` : '';

  const [full, setFull] = useState<ProjectionView | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [narrow, setNarrow] = useState<ProjectionView | null>(null);
  const [seenIdentity, setSeenIdentity] = useState(identityKey);
  const [seenDrill, setSeenDrill] = useState(drillKey);
  // Scope / horizon change resets; a reload (epoch bump) keeps full + drill until the refetch lands.
  if (seenIdentity !== identityKey) {
    setSeenIdentity(identityKey);
    setFull(null);
    setFailure(null);
    setNarrow(null);
  }
  if (seenDrill !== drillKey) {
    setSeenDrill(drillKey);
    setNarrow(null);
  }

  useEffect(() => {
    let cancelled = false;
    const current = toScope(kind, id, virtualAccountId, envelopeId, accountIds);
    void (async () => {
      try {
        // Today forward by the one window rule: `from` left out, one retry on a window refusal
        // (TMMAPP-0171 item 8). The horizon ends on or after today, so the read always runs.
        const { to } = horizonWindow(horizonMonths, today);
        const next = await readWindow<ProjectionView | null>(
          { from: today, to },
          today,
          (query) => fetchView(current, query),
          null,
        );
        if (cancelled) return;
        setFull(next);
        setFailure(null);
      } catch (error) {
        if (!cancelled) setFailure({ error });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, id, virtualAccountId, envelopeId, accountIds, horizonMonths, today, epoch]);

  useEffect(() => {
    if (drillFrom === undefined || drillTo === undefined) return;
    let cancelled = false;
    const current = toScope(kind, id, virtualAccountId, envelopeId, accountIds);
    void (async () => {
      try {
        // The one window rule (TMMAPP-0159 §0): a drill into the current month leaves `from` out;
        // one already over asks nothing, and the table keeps the full window.
        const next = await readWindow(
          { from: drillFrom, to: drillTo },
          today,
          (query) => fetchView(current, query),
          null,
        );
        if (!cancelled) setNarrow(next);
      } catch (error) {
        if (!cancelled) setFailure({ error });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [drillFrom, drillTo, today, kind, id, virtualAccountId, envelopeId, accountIds, epoch]);

  const reload = useCallback(async () => {
    setEpoch((n) => n + 1);
  }, []);

  const status: ProjectionStatus = failure !== null ? 'error' : full === null ? 'loading' : 'ready';
  const table = drillKey.length > 0 ? (narrow ?? full) : full;
  const fromToday =
    drillFrom !== undefined &&
    drillKey.length > 0 &&
    narrow !== null &&
    narrow.windowFrom > drillFrom;
  return { status, error: failure?.error, full, table, fromToday, reload };
}
